import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { api, formatApiError } from "../../lib/api";
import { Phone, Mail, Video, FileText, X, Activity as ActivityIcon, TrendingUp, CheckCircle2, Trophy, Receipt, ChevronRight } from "lucide-react";

const s = (v, fallback = "—") => {
    if (v == null || v === "") return fallback;
    if (typeof v === "string" || typeof v === "number") return v;
    return fallback;
};

const peso = (n) =>
    "₱" + Number(n || 0).toLocaleString("en-PH", { maximumFractionDigits: 0 });

const pesoShort = (n) => {
    const v = Number(n || 0);
    if (Math.abs(v) >= 1e6) return "₱" + (v / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
    if (Math.abs(v) >= 1e3) return "₱" + Math.round(v / 1e3) + "K";
    return "₱" + Math.round(v);
};

// quota attainment → color band
const attClass = (pct) => {
    if (pct == null) return { text: "text-muted-foreground", bar: "bg-slate-400/50" };
    if (pct >= 100) return { text: "text-emerald-500", bar: "bg-gradient-to-r from-emerald-500 to-emerald-400" };
    if (pct >= 70) return { text: "text-amber-500", bar: "bg-gradient-to-r from-amber-500 to-amber-400" };
    return { text: "text-rose-500", bar: "bg-gradient-to-r from-rose-500 to-rose-400" };
};

const initials = (name) => {
    const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "?";
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
};

// deterministic pleasant color from a name
const avatarStyle = (name) => {
    let h = 0;
    const str = String(name || "");
    for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) % 360;
    return { background: `linear-gradient(135deg, hsl(${h} 70% 55%), hsl(${(h + 40) % 360} 70% 45%))` };
};

function Avatar({ name, size = 40, ring }) {
    return (
        <div
            className="rounded-full flex items-center justify-center text-white font-semibold shrink-0 shadow-sm"
            style={{ width: size, height: size, fontSize: size * 0.38, ...avatarStyle(name), ...(ring ? { boxShadow: `0 0 0 3px ${ring}` } : {}) }}
        >
            {initials(name)}
        </div>
    );
}

const TYPE_ICON = { Call: Phone, Email: Mail, Meeting: Video };

// One-time reconciliation flag: SAP invoiced sales vs the external "By Rep" store
// summary for JULY 2026. Shows only when the report window overlaps that month and
// the store filter is All or one of the affected companies. See the July recon workbook.
const RECON_JULY = { from: "2026-07-01", to: "2026-07-31" };
const RECON_STORES = new Set(["all", "SBM LUXURY HOME TRADING INC", "MOSAICO ARTE"]);
function windowsOverlap(aFrom, aTo, bFrom, bTo) {
    return aFrom && aTo && aFrom <= bTo && aTo >= bFrom;
}
function ReconFlag({ dateFrom, dateTo, store }) {
    const [dismissed, setDismissed] = useState(false);
    if (dismissed) return null;
    if (!windowsOverlap(dateFrom, dateTo, RECON_JULY.from, RECON_JULY.to)) return null;
    if (store && !RECON_STORES.has(store)) return null;
    return (
        <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 md:p-4" data-testid="recon-flag-july2026">
            <div className="flex items-start gap-3">
                <div className="text-amber-600 text-lg leading-none mt-0.5">⚠</div>
                <div className="flex-1 text-sm">
                    <div className="font-semibold text-amber-700">July 2026 reconciliation flag — SAP is ahead of the “By Rep” store report by ₱3.55M</div>
                    <ul className="mt-1.5 space-y-1 text-foreground/90 list-disc pl-4">
                        <li><span className="font-medium">SBM · Ella Imperial:</span> report shows <span className="text-rose-600 font-medium">−₱2,790,862</span> but SAP shows <span className="text-emerald-600 font-medium">+₱231,050</span> — a <span className="font-semibold">₱3.02M</span> variance (mis-posted credit memo, not a real loss). ≈85% of the gap.</li>
                        <li><span className="font-medium">BGC Mosaico Arte · “Inhouse”:</span> ~₱198K of house-account sales missing from the report.</li>
                        <li>The remaining ~₱330K is timing — the report was pulled before month-end close (every agent is ~1% under SAP).</li>
                    </ul>
                    <div className="mt-1.5 text-xs text-muted-foreground">Note: branch-store agents are tracked in SAP Cost-Center dimension 2 (“Sales Agent”), not the U_SAgent field this leaderboard groups by — so they don’t appear as rows here.</div>
                </div>
                <button onClick={() => setDismissed(true)} className="p-1 rounded hover:bg-amber-500/20 text-amber-700 shrink-0" title="Dismiss">
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

function defaultRange() {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { from: fmt(from), to: fmt(to) };
}

export default function TeamActivity() {
    const initial = defaultRange();
    const [dateFrom, setDateFrom] = useState(initial.from);
    const [dateTo, setDateTo] = useState(initial.to);

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);

    const [drilldown, setDrilldown] = useState(null); // { email, name }
    const [view, setView] = useState("activity"); // "activity" | "sales"

    const load = async () => {
        setLoading(true);
        setErrored(false);
        try {
            const { data } = await api.get("/crm/activity-report", { params: { dateFrom, dateTo } });
            setReport(data && typeof data === "object" ? data : null);
        } catch (e) {
            setErrored(true);
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateFrom, dateTo]);

    const agents = Array.isArray(report?.agents) ? report.agents : [];
    const totals = report && typeof report.totals === "object" && report.totals ? report.totals : {};

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="crm-team-activity-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="sc-section-title">CRM</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Team Performance</h1>
                </div>
                <div className="inline-flex rounded-md border border-border overflow-hidden self-start">
                    <button
                        onClick={() => setView("activity")}
                        className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${view === "activity" ? "bg-secondary font-medium" : "text-muted-foreground hover:bg-secondary/50"}`}
                        data-testid="tab-activity"
                    >
                        <ActivityIcon className="w-3.5 h-3.5" /> Activity
                    </button>
                    <button
                        onClick={() => setView("sales")}
                        className={`px-3 py-1.5 text-sm flex items-center gap-1.5 border-l border-border ${view === "sales" ? "bg-secondary font-medium" : "text-muted-foreground hover:bg-secondary/50"}`}
                        data-testid="tab-sales"
                    >
                        <TrendingUp className="w-3.5 h-3.5" /> Sales (SAP)
                    </button>
                </div>
            </div>

            {view === "sales" ? (
                <SalesLeaderboard />
            ) : (
            <>
            <div className="sc-card p-3 flex flex-col md:flex-row md:items-center gap-3">
                <input type="date" className="sc-input md:w-44" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                <span className="text-xs text-muted-foreground hidden md:inline">to</span>
                <input type="date" className="sc-input md:w-44" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <SummaryCard label="Calls" value={totals.calls ?? "—"} />
                <SummaryCard label="Emails" value={totals.emails ?? "—"} />
                <SummaryCard label="Meetings" value={totals.meetings ?? "—"} />
                <SummaryCard label="Total" value={totals.total ?? "—"} />
                <SummaryCard label="Agents" value={report?.agent_count ?? agents.length} />
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[900px]">
                        <thead>
                            <tr>
                                <th>Agent</th>
                                <th>Branch</th>
                                <th className="text-right">Calls</th>
                                <th className="text-right">Emails</th>
                                <th className="text-right">Meetings</th>
                                <th className="text-right">Notes</th>
                                <th className="text-right">Open Tasks</th>
                                <th className="text-right">Total</th>
                                <th>Last Activity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">Loading…</td></tr>}
                            {!loading && errored && <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">Couldn't load the activity report.</td></tr>}
                            {!loading && !errored && agents.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">No agent activity in this range.</td></tr>}
                            {!loading && !errored && agents.map((a) => (
                                <tr
                                    key={a.owner_email}
                                    className="cursor-pointer"
                                    onClick={() => setDrilldown({ email: a.owner_email, name: a.owner_name })}
                                    data-testid={`team-agent-row-${a.owner_email}`}
                                >
                                    <td>
                                        <div className="font-medium">{s(a.owner_name)}</div>
                                        <div className="text-xs text-muted-foreground font-mono">{s(a.owner_email)}</div>
                                    </td>
                                    <td className="text-muted-foreground">{s(a.branch)}</td>
                                    <td className="sc-num">{a.calls ?? 0}</td>
                                    <td className="sc-num">{a.emails ?? 0}</td>
                                    <td className="sc-num">{a.meetings ?? 0}</td>
                                    <td className="sc-num">{a.notes ?? 0}</td>
                                    <td className="sc-num">{a.tasks_open ?? 0}</td>
                                    <td className="sc-num font-semibold">{a.total ?? 0}</td>
                                    <td className="text-xs text-muted-foreground">{a.last_activity_at ? new Date(a.last_activity_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            </>
            )}

            {drilldown && (
                <AgentDrilldown agent={drilldown} dateFrom={dateFrom} dateTo={dateTo} onClose={() => setDrilldown(null)} />
            )}
        </div>
    );
}

const PERIODS = [["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"], ["ytd", "Year to Date"]];
const MEDAL = { 0: { ring: "#f5c451", label: "1", grad: "from-amber-400/20 to-amber-500/5", badge: "bg-amber-400 text-amber-950" },
                1: { ring: "#cbd5e1", label: "2", grad: "from-slate-300/20 to-slate-400/5", badge: "bg-slate-300 text-slate-800" },
                2: { ring: "#e0a366", label: "3", grad: "from-orange-400/20 to-orange-500/5", badge: "bg-orange-400 text-orange-950" } };

function SalesLeaderboard() {
    const [period, setPeriod] = useState("ytd");
    const [store, setStore] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [data, setData] = useState(null);
    const [monthly, setMonthly] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [drill, setDrill] = useState(null); // { agent }

    const customActive = Boolean(dateFrom && dateTo);
    const periodLabel = customActive ? "range" : (period === "ytd" ? "YTD" : period);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            setErrored(false);
            try {
                const reportParams = customActive ? { dateFrom, dateTo, store } : { period, store };
                const [rep, mon] = await Promise.all([
                    api.get("/crm/sales-report", { params: reportParams }),
                    api.get("/crm/sales-monthly", { params: { store } }),
                ]);
                if (!alive) return;
                setData(rep.data && typeof rep.data === "object" ? rep.data : null);
                setMonthly(mon.data && typeof mon.data === "object" ? mon.data : null);
            } catch (e) {
                if (alive) { setErrored(true); toast.error(formatApiError(e)); }
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [period, store, dateFrom, dateTo, customActive]);

    const pickPeriod = (val) => { setDateFrom(""); setDateTo(""); setPeriod(val); };

    const agents = Array.isArray(data?.agents) ? data.agents : [];
    const allStores = Array.isArray(data?.all_stores) ? data.all_stores : [];
    const stores = Array.isArray(data?.stores) ? data.stores : [];
    const top3 = agents.slice(0, 3);
    const rest = agents.slice(3);
    const maxRest = rest.reduce((m, a) => Math.max(m, a.revenue || 0), 0) || 1;

    // team attainment = revenue of quota'd agents / their total quota target
    const quotaAgents = agents.filter((a) => a.quota_period);
    const quotaRev = quotaAgents.reduce((sum, a) => sum + (a.revenue || 0), 0);
    const teamAttainment = data?.quota_total ? (quotaRev / data.quota_total) * 100 : null;

    return (
        <div className="space-y-5">
            {/* controls */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <select
                    className="sc-input w-full lg:w-72"
                    value={store}
                    onChange={(e) => setStore(e.target.value)}
                    data-testid="sales-store-select"
                >
                    <option value="all">🏢 All Companies</option>
                    {allStores.map((st) => (
                        <option key={st.schema_name} value={st.schema_name}>{st.display_name}</option>
                    ))}
                </select>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="inline-flex rounded-md border border-border overflow-hidden self-start">
                        {PERIODS.map(([val, label], i) => (
                            <button
                                key={val}
                                onClick={() => pickPeriod(val)}
                                className={`px-3 py-1.5 text-sm ${i > 0 ? "border-l border-border" : ""} ${!customActive && period === val ? "bg-secondary font-medium" : "text-muted-foreground hover:bg-secondary/50"}`}
                                data-testid={`sales-period-${val}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <div className={`flex items-center gap-1.5 rounded-md border px-2 py-1 ${customActive ? "border-sky-500/60 bg-sky-500/5" : "border-border"}`}>
                        <input type="date" className="bg-transparent text-sm outline-none w-[8.5rem]" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} data-testid="sales-date-from" />
                        <span className="text-xs text-muted-foreground">→</span>
                        <input type="date" className="bg-transparent text-sm outline-none w-[8.5rem]" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} data-testid="sales-date-to" />
                        {customActive && (
                            <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="p-0.5 rounded hover:bg-secondary text-muted-foreground" title="Clear range">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {customActive && (
                <div className="text-xs text-muted-foreground -mt-2">
                    Custom range: {new Date(dateFrom).toLocaleDateString("en-PH", { timeZone: "Asia/Manila" })} → {new Date(dateTo).toLocaleDateString("en-PH", { timeZone: "Asia/Manila" })} · overriding the period preset
                </div>
            )}

            {/* July-2026 reconciliation flag (shown when the window overlaps that month) */}
            {!loading && data && (
                <ReconFlag dateFrom={data.date_from} dateTo={data.date_to} store={data.store} />
            )}

            {/* two-measures note */}
            <div className="sc-card px-3 py-2 text-[11px] text-muted-foreground leading-relaxed">
                Two measures on this tab. <span className="font-semibold text-foreground">Branch Revenue</span> is each branch company&apos;s own SAP invoiced revenue (its books). <span className="font-semibold text-foreground">Agent Ranking</span> is sales attributed to individual agents, which are billed through Dwellstone / Mozzaico, so the two totals will not match.
            </div>

            {/* summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <SummaryCard label={`Attributed Sales (${periodLabel})`} value={loading ? "…" : peso(data?.total_revenue)} />
                <SummaryCard label={`Team Quota (${periodLabel})`} value={loading ? "…" : (data?.quota_total ? peso(data.quota_total) : "—")} />
                <SummaryCard
                    label="Team Attainment"
                    value={loading ? "…" : (teamAttainment != null ? `${teamAttainment.toFixed(0)}%` : "—")}
                    valueClass={teamAttainment != null ? attClass(teamAttainment).text : ""}
                />
                <SummaryCard label="Agents Ranked" value={loading ? "…" : (data?.agent_count ?? agents.length)} />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
                {/* left: monthly chart */}
                <div className="xl:col-span-3 sc-card p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="text-sm font-semibold">Branch Revenue, company books</div>
                            <div className="text-[11px] text-muted-foreground">{store === "all" ? "Your branch companies, monthly SAP invoiced revenue" : (allStores.find(x => x.schema_name === store)?.display_name || store)}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{monthly?.year ?? ""} total</div>
                            <div className="text-sm font-semibold">{loading ? "…" : peso(monthly?.total_revenue)}</div>
                        </div>
                    </div>
                    <MonthlyChart series={monthly?.series} loading={loading} onPick={() => {}} />
                </div>

                {/* right: podium */}
                <div className="xl:col-span-2 sc-card p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <Trophy className="w-4 h-4 text-amber-500" />
                        <span className="text-sm font-semibold">Agent Ranking</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mb-3">Attributed sales, billed via Dwellstone / Mozzaico</div>
                    {loading ? (
                        <div className="text-center text-sm text-muted-foreground py-10">Loading…</div>
                    ) : top3.length === 0 ? (
                        <div className="text-center text-sm text-muted-foreground py-10">No invoiced sales.</div>
                    ) : (
                        <Podium top3={top3} onPick={(a) => setDrill({ agent: a.agent })} />
                    )}
                </div>
            </div>

            {/* ranked list (4+) */}
            {rest.length > 0 && (
                <div className="sc-card overflow-hidden">
                    <div className="px-4 pt-3 pb-2 flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Full Ranking</span>
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Bar = quota attainment</span>
                    </div>
                    <ul>
                        {rest.map((a, i) => {
                            const rank = i + 4;
                            const hasQuota = a.attainment_pct != null;
                            const barPct = hasQuota
                                ? Math.min(100, Math.max(2, Math.round(a.attainment_pct)))
                                : Math.max(3, Math.round(((a.revenue || 0) / maxRest) * 100));
                            const ac = attClass(a.attainment_pct);
                            return (
                                <li
                                    key={a.agent || i}
                                    onClick={() => setDrill({ agent: a.agent })}
                                    className="group flex items-center gap-3 px-4 py-2.5 border-t border-border cursor-pointer hover:bg-secondary/40"
                                    data-testid={`sales-agent-row-${rank}`}
                                >
                                    <span className="w-6 text-sm text-muted-foreground tabular-nums text-right">{rank}</span>
                                    <Avatar name={a.agent} size={32} />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium truncate">{s(a.agent)}</span>
                                            {a.matched_user && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" title={`CRM: ${a.matched_user.name || a.matched_user.email}`} />}
                                        </div>
                                        <div className="mt-1 h-1.5 rounded-full bg-secondary overflow-hidden" title={hasQuota ? `${Math.round(a.attainment_pct)}% of ${peso(a.quota_period)} quota` : "no quota set"}>
                                            <div className={`h-full rounded-full ${hasQuota ? ac.bar : "bg-gradient-to-r from-sky-500 to-sky-400"}`} style={{ width: `${barPct}%` }} />
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0 w-24">
                                        <div className="text-sm font-semibold tabular-nums">{peso(a.revenue)}</div>
                                        <div className="text-[11px] text-muted-foreground">
                                            {hasQuota ? <span className={ac.text}>{Math.round(a.attainment_pct)}% quota</span> : `${a.invoices ?? 0} inv`}
                                        </div>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {/* per-store breakdown (only meaningful across all companies) */}
            {store === "all" && stores.length > 0 && (
                <div className="sc-card p-4">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Attributed revenue by billing company · this period</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {stores.map((st) => (
                            <button
                                key={st.schema_name}
                                onClick={() => setStore(st.schema_name)}
                                className="flex items-center justify-between border border-border rounded-md px-3 py-2 text-left hover:bg-secondary/40"
                            >
                                <span className="text-sm truncate">{s(st.display_name, st.schema_name)}</span>
                                <span className="text-sm font-semibold tabular-nums ml-2">{peso(st.revenue)}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <p className="text-xs text-muted-foreground">
                {s(data?.basis, "Ranked by real SAP invoice revenue (INV1.U_SAgent).")} Click any agent to see the exact invoices.
            </p>

            {drill && (
                <AgentInvoicesDrawer
                    agent={drill.agent}
                    store={store}
                    onClose={() => setDrill(null)}
                />
            )}
        </div>
    );
}

function Podium({ top3, onPick }) {
    // display order: 2nd, 1st, 3rd — with 1st raised
    const order = [top3[1], top3[0], top3[2]];
    const rankIdx = [1, 0, 2];
    const heights = ["h-16", "h-24", "h-12"];
    return (
        <div className="flex items-end justify-center gap-2 sm:gap-3">
            {order.map((a, col) => {
                if (!a) return <div key={col} className="flex-1" />;
                const ri = rankIdx[col];
                const m = MEDAL[ri];
                return (
                    <button
                        key={a.agent || col}
                        onClick={() => onPick(a)}
                        className="flex-1 flex flex-col items-center min-w-0 group"
                        data-testid={`podium-${ri + 1}`}
                    >
                        <div className="relative">
                            <Avatar name={a.agent} size={ri === 0 ? 64 : 52} ring={m.ring} />
                            <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center ${m.badge} shadow`}>
                                {m.label}
                            </span>
                        </div>
                        <div className="mt-3 text-xs font-medium text-center leading-tight truncate w-full px-0.5">{s(a.agent)}</div>
                        <div className={`text-center font-bold tabular-nums ${ri === 0 ? "text-base" : "text-sm"}`}>{pesoShort(a.revenue)}</div>
                        {a.attainment_pct != null && (
                            <div className={`text-center text-[11px] font-semibold ${attClass(a.attainment_pct).text}`}>
                                {Math.round(a.attainment_pct)}% of quota
                            </div>
                        )}
                        <div className={`mt-1.5 w-full rounded-t-md bg-gradient-to-b ${m.grad} border-x border-t border-border ${heights[col]} group-hover:brightness-110`} />
                    </button>
                );
            })}
        </div>
    );
}

function MonthlyChart({ series, loading, onPick, selectedMonth, height = 264, emptyText = "No invoiced sales this year." }) {
    if (loading) return <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>Loading…</div>;
    const rows = Array.isArray(series) ? series : [];
    const hasData = rows.some((r) => (r.revenue || 0) > 0);
    if (!hasData) return <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>{emptyText}</div>;
    const clickable = typeof onPick === "function";
    return (
        <div style={{ width: "100%", height }}>
            <ResponsiveContainer>
                <BarChart data={rows} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
                    <defs>
                        <linearGradient id="barBlue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#38bdf8" />
                            <stop offset="100%" stopColor="#0ea5e9" />
                        </linearGradient>
                        <linearGradient id="barDim" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.35" />
                            <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.35" />
                        </linearGradient>
                    </defs>
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "currentColor", opacity: 0.6 }} tickLine={false} axisLine={false} />
                    <YAxis tickFormatter={pesoShort} tick={{ fontSize: 10, fill: "currentColor", opacity: 0.5 }} tickLine={false} axisLine={false} width={44} />
                    <Tooltip
                        cursor={{ fill: "currentColor", opacity: 0.06 }}
                        contentStyle={{ background: "var(--card, #0b1220)", border: "1px solid rgba(148,163,184,.3)", borderRadius: 8, fontSize: 12 }}
                        labelStyle={{ fontWeight: 600 }}
                        formatter={(v) => [peso(v), "Revenue"]}
                    />
                    <Bar
                        dataKey="revenue"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={40}
                        cursor={clickable ? "pointer" : "default"}
                        onClick={clickable ? (_, i) => onPick(rows[i]?.month) : undefined}
                    >
                        {rows.map((r, i) => (
                            <Cell key={i} fill={selectedMonth && r.month !== selectedMonth ? "url(#barDim)" : "url(#barBlue)"} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthOf = (isoDate) => parseInt(String(isoDate || "").slice(5, 7), 10) || 0;

function AgentInvoicesDrawer({ agent, store, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [month, setMonth] = useState(null); // selected month (1-12) or null = all

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            setErrored(false);
            setMonth(null);
            try {
                // always pull the whole year so we can show the monthly breakdown
                const { data } = await api.get("/crm/sales-agent-invoices", { params: { agent, store, period: "ytd" } });
                if (alive) setData(data && typeof data === "object" ? data : null);
            } catch (e) {
                if (alive) setErrored(true);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [agent, store]);

    const invoices = Array.isArray(data?.invoices) ? data.invoices : [];
    const multiStore = store === "all";

    // bucket the agent's own invoices by month
    const byMonth = {};
    for (let m = 1; m <= 12; m++) byMonth[m] = { revenue: 0, count: 0 };
    for (const iv of invoices) {
        const m = monthOf(iv.doc_date);
        if (m >= 1 && m <= 12) { byMonth[m].revenue += iv.amount || 0; byMonth[m].count += 1; }
    }
    const series = MONTH_LABELS.map((label, i) => ({ month: i + 1, label, revenue: Math.round(byMonth[i + 1].revenue) }));

    const shown = month ? invoices.filter((iv) => monthOf(iv.doc_date) === month) : invoices;
    const shownTotal = shown.reduce((sum, iv) => sum + (iv.amount || 0), 0);
    const shownGP = shown.reduce((sum, iv) => sum + (iv.gross_profit || 0), 0);

    const pick = (m) => setMonth((cur) => (cur === m ? null : m));

    return (
        <div className="fixed inset-0 bg-black/40 flex justify-end z-50" onClick={onClose}>
            <div
                className="w-full max-w-2xl h-full bg-background overflow-y-auto border-l border-border"
                onClick={(e) => e.stopPropagation()}
                data-testid="agent-invoices-drawer"
            >
                <div className="px-4 py-3 border-b border-border sticky top-0 bg-background z-10">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                            <Avatar name={agent} size={40} />
                            <div className="min-w-0">
                                <div className="text-base font-semibold truncate">{s(agent)}</div>
                                <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                                    <Receipt className="w-3 h-3" /> Monthly sales · {data?.date_from ? new Date(data.date_from).getFullYear() : "year"} to date
                                    {multiStore ? " · all companies" : ""}
                                </div>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-1 rounded hover:bg-secondary text-muted-foreground shrink-0">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    {!loading && data && (
                        <div className="grid grid-cols-3 gap-2 mt-3">
                            <MiniStat label={month ? `${MONTH_LABELS[month - 1]} Invoices` : "Invoices"} value={shown.length} />
                            <MiniStat label={month ? `${MONTH_LABELS[month - 1]} Revenue` : "Revenue (YTD)"} value={peso(shownTotal)} />
                            <MiniStat label="Gross Profit" value={peso(shownGP)} />
                        </div>
                    )}
                </div>

                <div className="p-4 space-y-4">
                    {loading && <div className="text-center text-sm text-muted-foreground py-10">Loading agent sales…</div>}
                    {!loading && errored && <div className="text-center text-sm text-muted-foreground py-10">Couldn't load this agent's sales.</div>}
                    {!loading && !errored && invoices.length === 0 && <div className="text-center text-sm text-muted-foreground py-10">No invoices this year.</div>}

                    {!loading && !errored && invoices.length > 0 && (
                        <>
                            <div className="sc-card p-3">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="text-xs font-semibold">Monthly Sales</div>
                                    <div className="text-[11px] text-muted-foreground">Click a bar to filter transactions</div>
                                </div>
                                <MonthlyChart
                                    series={series}
                                    onPick={pick}
                                    selectedMonth={month}
                                    height={180}
                                    emptyText="No invoiced sales this year."
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="text-xs font-semibold">
                                    {month ? `${MONTH_LABELS[month - 1]} transactions` : "All transactions"}
                                    <span className="text-muted-foreground font-normal"> · {shown.length}</span>
                                </div>
                                {month && (
                                    <button onClick={() => setMonth(null)} className="text-[11px] text-sky-500 hover:underline flex items-center gap-1">
                                        <X className="w-3 h-3" /> clear month
                                    </button>
                                )}
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full sc-table">
                                    <thead>
                                        <tr>
                                            <th>Invoice</th>
                                            <th>Date</th>
                                            <th>Customer</th>
                                            {multiStore && <th>Company</th>}
                                            <th className="text-right">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {shown.map((iv, i) => (
                                            <tr key={`${iv.schema_name}-${iv.doc_num}-${i}`}>
                                                <td className="font-mono text-xs">#{s(iv.doc_num)}</td>
                                                <td className="text-xs text-muted-foreground whitespace-nowrap">{iv.doc_date ? new Date(iv.doc_date).toLocaleDateString("en-PH", { timeZone: "Asia/Manila" }) : "—"}</td>
                                                <td className="text-sm">{s(iv.customer)}</td>
                                                {multiStore && <td className="text-xs text-muted-foreground">{s(iv.store)}</td>}
                                                <td className="sc-num font-semibold tabular-nums">{peso(iv.amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function MiniStat({ label, value }) {
    return (
        <div className="rounded-md border border-border px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-sm font-semibold mt-0.5 tabular-nums">{value}</div>
        </div>
    );
}

function AgentDrilldown({ agent, dateFrom, dateTo, onClose }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [expanded, setExpanded] = useState(() => new Set());

    useEffect(() => {
        (async () => {
            setLoading(true);
            setErrored(false);
            try {
                const { data } = await api.get("/crm/activities", {
                    params: { ownerEmail: agent.email, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined },
                });
                setRows(Array.isArray(data) ? data : []);
            } catch (e) {
                setErrored(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [agent.email, dateFrom, dateTo]);

    const toggle = (id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex justify-end z-50" onClick={onClose}>
            <div
                className="sc-card w-full max-w-2xl h-full bg-background overflow-y-auto rounded-none border-l border-border"
                onClick={(e) => e.stopPropagation()}
                data-testid="agent-drilldown-panel"
            >
                <div className="px-4 py-3 border-b border-border flex items-center justify-between sticky top-0 bg-background">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">Agent Activity</div>
                        <div className="text-lg font-semibold">{s(agent.name, agent.email)}</div>
                    </div>
                    <button onClick={onClose} className="p-1 rounded hover:bg-secondary text-muted-foreground">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-4">
                    {loading && <div className="text-center text-sm text-muted-foreground py-8">Loading…</div>}
                    {!loading && errored && <div className="text-center text-sm text-muted-foreground py-8">Couldn't load this agent's activity.</div>}
                    {!loading && !errored && rows.length === 0 && <div className="text-center text-sm text-muted-foreground py-8">No activity in this range.</div>}

                    {!loading && !errored && rows.length > 0 && (
                        <ul className="space-y-2">
                            {rows.map((a) => {
                                const Icon = TYPE_ICON[a.type] || FileText;
                                const isOpen = expanded.has(a.id);
                                const hasPresented = (a.presented_quotation_nos || []).length > 0 || (a.presented_items || []).length > 0 || a.presented_url || a.presented_notes;

                                return (
                                    <li key={a.id} className="border border-border rounded-md p-3">
                                        <div className="flex items-start gap-2">
                                            <Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-medium truncate">{s(a.subject)}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    {s(a.type)} · {s(a.related_type, "")}{a.related_label ? `: ${s(a.related_label)}` : ""}
                                                </div>
                                                <div className="text-xs text-muted-foreground font-mono">
                                                    {a.occurred_at ? new Date(a.occurred_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "—"}
                                                </div>
                                            </div>
                                        </div>

                                        {(a.pitch || hasPresented) && (
                                            <button onClick={() => toggle(a.id)} className="text-xs text-muted-foreground underline underline-offset-2 mt-2">
                                                {isOpen ? "Hide details" : "Show pitch / presented"}
                                            </button>
                                        )}

                                        {isOpen && (
                                            <div className="mt-2 text-xs space-y-2 bg-secondary/30 rounded-md p-3">
                                                {a.pitch && <div className="whitespace-pre-wrap">{s(a.pitch)}</div>}
                                                {(a.presented_quotation_nos || []).length > 0 && (
                                                    <div className="flex flex-wrap gap-1">
                                                        {a.presented_quotation_nos.map((no, i) => (
                                                            <span key={i} className="sc-pill bg-background border border-border font-mono">{s(no)}</span>
                                                        ))}
                                                    </div>
                                                )}
                                                {(a.presented_items || []).length > 0 && (
                                                    <div className="flex flex-wrap gap-1">
                                                        {a.presented_items.map((it, i) => (
                                                            <span key={i} className="sc-pill bg-background border border-border">
                                                                <span className="font-mono">{s(it?.item_code)}</span>{it?.item_name ? ` · ${s(it.item_name)}` : ""}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                {a.presented_url && (
                                                    <a href={a.presented_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all block">{s(a.presented_url)}</a>
                                                )}
                                                {a.presented_notes && <div className="whitespace-pre-wrap">{s(a.presented_notes)}</div>}
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

function SummaryCard({ label, value, valueClass = "" }) {
    return (
        <div className="sc-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`text-lg font-semibold mt-1 ${valueClass}`}>{value}</div>
        </div>
    );
}
